import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { type AppRole } from "@/contexts/AuthContext";
import { homeForRole, canRoleAccess } from "@/lib/roleRoutes";

/**
 * Returns a function that performs the post-auth redirect.
 *
 * Order of preference:
 *   1. `?next=/path` query param (handy for email links).
 *      `next` may include its own querystring AND/OR hash, e.g.
 *      `?next=/app/settings?tab=billing#payments` or `?next=/app#section`.
 *   2. `location.state.from` saved by RequireAuth (also preserves
 *      querystring + hash).
 *   3. Role's default home.
 *
 * Validation always runs against the *pathname only* (querystring and
 * hash are stripped before the allowlist check), but the chosen target
 * is navigated to with its querystring and hash intact.
 *
 * The saved `from` state is cleared so a refresh on the destination
 * doesn't re-trigger the redirect logic.
 */
export function usePostAuthRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (role: AppRole | null) => {
      const t0 = performance.now();
      const isManualOnlyTarget = (path: string) => {
        const pathname = path.split(/[?#]/, 1)[0];
        return pathname === "/admin" || pathname.startsWith("/admin/");
      };

      const params = new URLSearchParams(location.search);
      const queryNext = params.get("next"); // already URL-decoded by URLSearchParams
      const stateFrom = (location.state as { from?: string } | null)?.from;

      const candidates = [queryNext, stateFrom].filter(
        (p): p is string => !!p && p.startsWith("/") && !p.startsWith("//"),
      );

      const firstSaved = candidates[0];
      const allowedTarget = candidates.find(
        (p) => !isManualOnlyTarget(p) && canRoleAccess(role, p),
      );
      const target = allowedTarget ?? homeForRole(role);

      // Show a friendly toast if the saved destination wasn't allowed
      if (firstSaved && !allowedTarget) {
        const roleLabel = role ? role.charAt(0).toUpperCase() + role.slice(1) : "Guest";
        toast.info("Redirected to your dashboard", {
          description: `That page isn't available for ${roleLabel} accounts.`,
        });
      }

      // `target` is a path-and-search string (e.g. "/app/settings?tab=billing").
      // navigate() accepts that form directly and preserves the querystring.
      navigate(target, { replace: true, state: null });
      console.log(`[timing] postAuthRedirect chose=${target} in ${(performance.now() - t0).toFixed(1)}ms`);
    },
    [navigate, location.search, location.state],
  );
}

// Re-export for backward compatibility with existing imports.
export { homeForRole };
